/**
 * 屏幕 4：五行阶级金字塔
 *
 * 设计（最新）：
 *  - 5 阶：一等(1人) / 二等(2人) / 三等(3人) / 四等(4人) / 五等(5人)
 *  - 人偶按"先来者优先 → PK 抢占"排位
 *  - 每个人偶都是"挑战链接节点"：点击 → 弹出"选择挑战对象" → 弹出对波浮层
 *  - 无 4 字成语、无椭圆原木
 *  - 底部"返回"按钮
 */

import { createSnow } from "../snow";
import { applyDuelResult, getZoo, refreshZoo } from "../zoo";
import { drawFigure } from "../figureSprite";
import { hashStr } from "../hashUtil";
import { showDuelOverlay } from "../duelOverlay";
import {
  applyRanking,
  RANK_CAPS,
  RANK_LABELS,
  type RankLevel,
  type RankedEntry,
} from "../ranking";
import type { Element } from "../../shared/types";
import type { Fighter } from "../duel";

export interface Screen4Deps {
  root: HTMLElement;
  onBack: () => void;
}

export interface Screen4Controller {
  destroy: () => void;
}

/** 每阶占位宽度（像素）— 让上层窄，下层宽 */
const TIER_WIDTHS: Record<RankLevel, number> = {
  1: 180,
  2: 280,
  3: 380,
  4: 480,
  5: 580,
};

export function createScreen4(deps: Screen4Deps): Screen4Controller {
  const { root, onBack } = deps;
  const destroySnow = createSnow(root);

  const screen = document.createElement("div");
  screen.className = "screen-4";
  root.appendChild(screen);

  const title = document.createElement("div");
  title.className = "title";
  title.textContent = "五行阶级";
  screen.appendChild(title);

  const sub = document.createElement("div");
  sub.className = "subtitle";
  sub.textContent = "FIVE ELEMENTS · HIERARCHY";
  screen.appendChild(sub);

  const hint = document.createElement("div");
  hint.className = "duel-hint";
  hint.textContent = "· 点击人偶即可发起对波挑战 ·";
  screen.appendChild(hint);

  // 进入图鉴前从服务器拉一次最新数据，确保看到别人的提交
  void refreshZoo();

  // 当前金字塔引用（重建时会替换）
  let currentPyramid: HTMLDivElement | null = null;

  /**
   * 构建/重建金字塔
   *  - 每次重新调用：先销毁旧金字塔再构建
   *  - 用于对波胜出后 swapRanks 派发的 "zoo:changed" 事件
   */
  const buildPyramid = () => {
    if (currentPyramid && currentPyramid.parentNode) {
      currentPyramid.parentNode.removeChild(currentPyramid);
      currentPyramid = null;
    }

    // 排位（先来者优先 + PK）
    const entries = getZoo();
    const result = applyRanking(entries);
    const tiers = result.tiers;

    // 把全部人偶拉平（除当前点击目标外的可选）
    const allFighters: RankedEntry[] = [];
    for (const r of [1, 2, 3, 4, 5] as RankLevel[]) {
      allFighters.push(...tiers[r]);
    }

    const pyramid = document.createElement("div");
    pyramid.className = "pyramid";

    for (const rank of [1, 2, 3, 4, 5] as RankLevel[]) {
      const list = tiers[rank];
      const layer = document.createElement("div");
      layer.className = `pyramid-layer layer-rank-${rank}`;
      layer.style.width = `${TIER_WIDTHS[rank]}px`;

      const header = document.createElement("div");
      header.className = "layer-header";
      header.innerHTML = `
        <span class="layer-rank">${RANK_LABELS[rank]}</span>
        <span class="layer-count">${list.length}/${RANK_CAPS[rank]}</span>
      `;
      layer.appendChild(header);

      const figs = document.createElement("div");
      figs.className = "layer-figures";
      if (list.length === 0) {
        const empty = document.createElement("div");
        empty.className = "layer-empty";
        empty.textContent = "· 虚位 ·";
        figs.appendChild(empty);
      } else {
        const slotW = TIER_WIDTHS[rank] / RANK_CAPS[rank];
        list.forEach((re, idx) => {
          const fig = document.createElement("button");
          fig.className = `figure figure-clickable element-${re.element}`;
          fig.style.left = `${(idx + 0.5) * slotW}px`;
          fig.style.transform = "translateX(-50%)";

          const canvas = document.createElement("canvas");
          canvas.className = "figure-canvas";
          canvas.width = 32;
          canvas.height = 32;
          drawFigure(canvas, re.element as Element, rank, hashStr(re.scpId));
          fig.appendChild(canvas);

          // 名人 buff 小标志（金色"名"字方框）
          if (re.fameHit) {
            const fameBadge = document.createElement("div");
            fameBadge.className = "fame-mini-badge";
            fameBadge.textContent = "名";
            fameBadge.title = `【${re.fameLabel}】+${re.fameScoreBonus}分`;
            fig.appendChild(fameBadge);
          }

          const name = document.createElement("div");
          name.className = "figure-name";
          name.textContent = re.name;
          fig.appendChild(name);

          fig.title = `【${RANK_LABELS[rank]}】${re.name} · 五行 ${re.element} · 分 ${re.total}\n点击选择挑战对象`;

          fig.addEventListener("click", () => {
            openTargetPicker(re, allFighters, (target) => {
              // 角色定义：
              //   re = 用户在金字塔中点击的人偶 = 被挑战者（defender，守擂）
              //   target = 用户从 picker 选中的对手 = 挑战者（challenger，攻擂）
              const defender: Fighter = {
                scpId: re.scpId,
                name: re.name,
                element: re.element as Element,
                rank,
                total: re.total,
                rankLabel: RANK_LABELS[rank],
              };
              const challenger: Fighter = {
                scpId: target.scpId,
                name: target.name,
                element: target.element as Element,
                rank: target.rank,
                total: target.total,
                rankLabel: RANK_LABELS[target.rank],
              };
              showDuelOverlay({
                challenger,
                defender,
                onClose: () => { /* 浮层自带关闭 */ },
                onDuelResult: (payload) => {
                  // === 互换阶层：胜者与败者交换 fortune.total（让 applyRanking 重排时胜者排前）===
                  applyDuelResult(payload.winnerScpId, payload.loserScpId);
                },
              });
            });
          });
          figs.appendChild(fig);
        });
        const emptySlots = RANK_CAPS[rank] - list.length;
        for (let i = 0; i < emptySlots; i++) {
          const slot = document.createElement("div");
          slot.className = "figure-slot";
          slot.style.left = `${(list.length + i + 0.5) * slotW}px`;
          slot.style.transform = "translateX(-50%)";
          slot.innerHTML = `<div class="figure-slot-mark">·</div>`;
          figs.appendChild(slot);
        }
      }
      layer.appendChild(figs);
      pyramid.appendChild(layer);
    }
    screen.appendChild(pyramid);
    currentPyramid = pyramid;
  };

  // 首次构建
  buildPyramid();

  // === 监听 zoo:changed 事件（对波胜出后 swapRanks 派发）→ 重建金字塔 ===
  const onZooChanged = () => {
    try { buildPyramid(); } catch { /* ignore */ }
  };
  window.addEventListener("zoo:changed", onZooChanged);

  // 底部大号返回按钮
  const back = document.createElement("button");
  back.className = "screen4-back-big";
  back.innerHTML = `
    <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <rect x="9" y="2" width="2" height="12"/>
      <rect x="3" y="6" width="10" height="2"/>
      <rect x="2" y="5" width="2" height="4"/>
    </svg>
    <span>返回</span>
  `;
  back.addEventListener("click", onBack);
  screen.appendChild(back);

  return {
    destroy: () => {
      // 解除全局事件监听（重要！防止内存泄漏 / 不再监听后还触发）
      try { window.removeEventListener("zoo:changed", onZooChanged); } catch { /* ignore */ }
      destroySnow();
      if (screen.parentNode) screen.parentNode.removeChild(screen);
    },
  };
}

/**
 * 弹出"选择挑战对象"小浮层
 */
function openTargetPicker(
  challenger: RankedEntry,
  candidates: RankedEntry[],
  onPick: (target: RankedEntry) => void
) {
  const targets = candidates.filter((c) => c.scpId !== challenger.scpId);
  if (targets.length === 0) {
    const empty = document.createElement("div");
    empty.className = "picker-overlay";
    empty.innerHTML = `
      <div class="picker-box">
        <div class="picker-title">暂无可挑战的对手</div>
        <div class="picker-hint">需要至少 2 名角色才能发起对波</div>
        <button class="picker-ok">确定</button>
      </div>
    `;
    document.body.appendChild(empty);
    empty.querySelector(".picker-ok")!.addEventListener("click", () => empty.remove());
    return;
  }

  const overlay = document.createElement("div");
  overlay.className = "picker-overlay";

  const box = document.createElement("div");
  box.className = "picker-box";

  const ttl = document.createElement("div");
  ttl.className = "picker-title";
  ttl.innerHTML = `<span>谁要来挑战</span><span class="picker-self">「${challenger.name}」</span><span>？</span>`;
  box.appendChild(ttl);

  const grid = document.createElement("div");
  grid.className = "picker-grid";
  box.appendChild(grid);

  // 按阶位从高到低排
  const sorted = [...targets].sort((a, b) => a.rank - b.rank);

  for (const t of sorted) {
    const card = document.createElement("button");
    card.className = `picker-card element-${t.element}`;
    card.innerHTML = `
      <span class="pc-rank">${RANK_LABELS[t.rank]}</span>
      <span class="pc-name">${t.name}</span>
      <span class="pc-meta">${t.element} · 分 ${t.total}</span>
    `;
    card.addEventListener("click", () => {
      overlay.remove();
      onPick(t);
    });
    grid.appendChild(card);
  }

  const cancel = document.createElement("button");
  cancel.className = "picker-cancel";
  cancel.textContent = "取消";
  cancel.addEventListener("click", () => overlay.remove());
  box.appendChild(cancel);

  overlay.appendChild(box);
  document.body.appendChild(overlay);

  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) overlay.remove();
  });
}